import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTmrAuth, TMR_PORTAL_PERMISSIONS } from './clerk';
import type { TmrAuthStatus, TmrUser } from './clerk';
import { authzBackendEnabled, fetchAuthorization, TMR_PORTAL_PERMISSIONS_BACKEND } from './authorization';
import type {
  ActiveContext,
  AuthorizationScope,
  EnvironmentCapabilities,
  ResolvedAuthorization,
} from './authorization';
import { useTmrSession } from './useTmrSession';
import type { LocalLoginCredentials, TmrSession } from './tmrSession';

/** Where the effective permissions/roles came from. */
export type AuthorizationSource = 'backend' | 'organizations';

export type AuthorizationFetchStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseAuthorizationResult {
  /** Whether the backend authorization path is enabled (VITE_AUTHZ_BACKEND). */
  enabled: boolean;
  status: AuthorizationFetchStatus;
  data: ResolvedAuthorization | null;
  error: Error | null;
  /** Asks for the payload again after a failure, without ending the session. */
  reload: () => void;
}

/**
 * Fetches `GET /me/authorization` once the TMR session is active, when the backend path is enabled.
 * Disabled (flag off) → stays idle with no data, and callers fall back to Clerk Organizations.
 *
 * It keys off the **session**, not off Clerk being signed in: a break-glass operator has a perfectly
 * good session and no Clerk user at all, and would otherwise never get their permissions.
 *
 * @param sessionActive Whether {@link useTmrSession} reports an active session.
 * @param sessionUserId The signed-in operator, so re-fetching happens when the person changes.
 */
function useAuthorizationFetch(sessionActive: boolean, sessionUserId: string | null): UseAuthorizationResult {
  const enabled = authzBackendEnabled();

  const [state, setState] = useState<{
    status: AuthorizationFetchStatus;
    data: ResolvedAuthorization | null;
    error: Error | null;
  }>({ status: 'idle', data: null, error: null });

  // Bumped by `reload`. A failure here is usually a blip on the wire, and the screen it produces reads
  // as "you have no permissions" — so asking again has to be possible without ending the session.
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!enabled || !sessionActive) {
      setState({ status: 'idle', data: null, error: null });
      return;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));

    void (async () => {
      try {
        const data = await fetchAuthorization();
        if (!cancelled) setState({ status: 'ready', data, error: null });
      } catch (error) {
        if (!cancelled) {
          setState({
            status: 'error',
            data: null,
            error: error instanceof Error ? error : new Error(String(error)),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, sessionActive, sessionUserId, attempt]);

  return { enabled, status: state.status, data: state.data, error: state.error, reload };
}

/**
 * What a screen sees before the authorization payload arrives, and on the Organizations path.
 *
 * Nothing is available until the environment says it is. The opposite default would light up a screen
 * that then 404s on its first request — which is exactly the confusion the flag exists to remove.
 */
const NO_CAPABILITIES: EnvironmentCapabilities = { accessRequests: false };

/**
 * Session + authorization, from whichever source is active. Shape mirrors `useTmrAuth` so the
 * portals change almost nothing — only the data source. With the flag off it is exactly the
 * legacy Organizations behavior; with it on, permissions/roles/scope come from the backend.
 */
export interface TmrAuthz {
  status: TmrAuthStatus;
  /** The Clerk user, when the operator signed in through Clerk; null on the break-glass path. */
  user: TmrUser | null;
  /**
   * The operator behind the TMR session cookie — present whichever way they signed in, so this, not
   * {@link user}, is what a portal should render an identity from (backend source only).
   */
  session: TmrSession | null;
  roles: string[];
  permissions: string[];
  /** Empresa/Holding scope (backend source only); null on the Organizations path. */
  scope: AuthorizationScope | null;
  /** The slice of scope the operator is viewing (10 B6c); null when none is chosen or on the Organizations path. */
  activeContext: ActiveContext | null;
  /**
   * What this deployment is wired for, regardless of who is asking (backend source only).
   *
   * Everything is false until the payload arrives, and on the Organizations path — a screen that reads a
   * capability is asking "does this exist here?", and "not that we know of" is the safe answer while the
   * question is unanswered.
   */
  capabilities: EnvironmentCapabilities;
  source: AuthorizationSource;
  /** Set when the backend authorization fetch failed (backend source only). */
  authzError: Error | null;
  /** Asks for the authorization payload again after a failure, keeping the session. */
  reloadAuthorization: () => void;
  /** Set when signing in failed for a reason worth showing — including a break-glass rejection. */
  sessionError: Error | null;
  /**
   * Whether the session can still be rebuilt from Clerk — true when Clerk is signed in.
   *
   * A portal that is signed out with this true must **not** draw the password form: Clerk refuses to
   * sign in a client that already holds a session, so every attempt comes back as bad credentials.
   * {@link recoverSession} is the way through, and {@link signOut} is the way to start over.
   */
  canRecoverSession: boolean;
  /** Rebuilds the TMR session from the cookie, then from Clerk. Resolves true when one is active. */
  recoverSession: () => Promise<boolean>;
  /** Signs in with break-glass credentials (05 I3). Rejects with the message to show. */
  signInLocally: (credentials: LocalLoginCredentials) => Promise<void>;
  /** Revokes the TMR session server-side, then signs out of Clerk. */
  signOut: () => Promise<void>;
  /** Current Clerk session JWT getter, for calling protected APIs (e.g. admin tooling). */
  getToken: () => Promise<string | null>;
}

export function useTmrAuthz(): TmrAuthz {
  const auth = useTmrAuth();
  const tmrSession = useTmrSession();
  const backend = useAuthorizationFetch(tmrSession.status === 'active', tmrSession.session?.userId ?? null);
  const { getToken: clerkGetToken } = useAuth();
  const getToken = () => clerkGetToken();

  if (!backend.enabled) {
    return {
      status: auth.status,
      user: auth.user,
      session: null,
      roles: auth.roles,
      permissions: auth.permissions,
      scope: null,
      activeContext: null,
      capabilities: NO_CAPABILITIES,
      source: 'organizations',
      authzError: null,
      reloadAuthorization: () => {},
      sessionError: null,
      canRecoverSession: false,
      recoverSession: () => Promise.resolve(false),
      signInLocally: () => Promise.reject(new Error('Local sign-in requires the backend authorization path.')),
      signOut: auth.signOut,
      getToken,
    };
  }

  return {
    status: resolveStatus(auth.status, tmrSession.status, backend.status),
    user: auth.user,
    session: tmrSession.session,
    roles: backend.data?.roles ?? [],
    permissions: backend.data?.permissions ?? [],
    scope: backend.data?.scope ?? null,
    activeContext: backend.data?.activeContext ?? null,
    capabilities: backend.data?.capabilities ?? NO_CAPABILITIES,
    source: 'backend',
    authzError: backend.error,
    reloadAuthorization: backend.reload,
    sessionError: tmrSession.error,
    canRecoverSession: tmrSession.canRecover,
    recoverSession: tmrSession.recover,
    signInLocally: tmrSession.signInLocally,
    signOut: tmrSession.signOut,
    getToken,
  };
}

/**
 * Folds the three states a portal must not render between into one.
 *
 * The **TMR session** decides whether someone is signed in, not Clerk: a break-glass operator has no
 * Clerk user, and a Clerk user whose TMR session was revoked is not signed in any more. Clerk still
 * owns `sso-callback`, which is a route the app has to render before any of this applies.
 */
function resolveStatus(
  clerkStatus: TmrAuthStatus,
  sessionStatus: 'loading' | 'active' | 'anonymous',
  authzStatus: AuthorizationFetchStatus,
): TmrAuthStatus {
  if (clerkStatus === 'sso-callback') return 'sso-callback';
  if (sessionStatus === 'loading') return 'loading';
  if (sessionStatus === 'anonymous') return 'signed-out';
  // The session is up but the permissions have not landed: reporting 'signed-in' here would let the
  // portal decide access against an empty permission array and bounce the operator for a moment.
  return authzStatus === 'ready' || authzStatus === 'error' ? 'signed-in' : 'loading';
}

/** Required portal permissions for the active source (backend = dotted keys, legacy = `org:` keys). */
export function portalPermissions(portalKey: string, source: AuthorizationSource): string[] {
  return source === 'backend'
    ? TMR_PORTAL_PERMISSIONS_BACKEND[portalKey] ?? []
    : TMR_PORTAL_PERMISSIONS[portalKey] ?? [];
}
